/**
 * Minimal Markdown renderer for the docs viewer.
 *
 * **Security posture (PLAN.md §12):** raw HTML in the source is *never* injected. There is no
 * `dangerouslySetInnerHTML` anywhere in this module — every output is a React element or a text
 * node, so any `<script>` or `onerror=` in the Markdown renders as visible text rather than
 * executing. That property is what makes the same pipeline safe to reuse later for
 * user-authored content (ticket notes, portal submissions, email bodies), which is untrusted by
 * definition.
 *
 * Supported: headings (with slug anchors), bold/italic/inline code, links, fenced code blocks,
 * pipe tables, ordered/unordered lists, blockquotes, horizontal rules. Internal `.md` links are
 * rewritten to `/docs/:slug` routes.
 */

import type { ReactNode } from 'react';

import { CodeBlock } from '@/pages/DocsViewer';

export interface Heading {
  id: string;
  text: string;
  level: number;
}

export interface RenderedMarkdown {
  blocks: ReactNode[];
  headings: Heading[];
}

/** GitHub-style anchor slug, de-duplicated by the caller. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

const HEADING_CLASSES: Record<number, string> = {
  1: 'text-2xl font-semibold text-surface-900 dark:text-surface-100 mt-8 mb-3 scroll-mt-20',
  2: 'text-lg font-semibold text-surface-900 dark:text-surface-100 mt-8 mb-2 scroll-mt-20',
  3: 'text-base font-semibold text-surface-900 dark:text-surface-100 mt-6 mb-2 scroll-mt-20',
  4: 'text-sm font-semibold text-surface-800 dark:text-surface-200 mt-4 mb-1 scroll-mt-20',
  5: 'text-sm font-semibold text-surface-700 dark:text-surface-300 mt-3 mb-1 scroll-mt-20',
  6: 'text-xs font-semibold uppercase tracking-wide text-surface-600 dark:text-surface-400 mt-3 mb-1 scroll-mt-20',
};

/**
 * Inline formatting. Splits on markers and returns React nodes — the input string is only ever
 * placed into text positions, so nothing here can produce markup from the source.
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Order matters: code first so `**` inside backticks isn't treated as bold.
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-i${index++}`;

    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-surface-100 px-1 py-0.5 font-mono text-[0.85em] text-surface-800 dark:bg-surface-800 dark:text-surface-200"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(
        <strong key={key} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        const [, label = '', rawHref = ''] = linkMatch;
        nodes.push(renderLink(label, rawHref, key));
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(
        <em key={key} className="italic">
          {token.slice(1, -1)}
        </em>,
      );
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderLink(label: string, rawHref: string, key: string): ReactNode {
  const href = rawHref.trim();

  // Internal .md links become /docs/:slug routes. Checked *before* the protocol allowlist,
  // because these are bare relative filenames (ADMIN_GUIDE.md) and this branch constructs its
  // own href rather than passing the source value through.
  const mdMatch = /^([A-Z_]+)\.md(#.*)?$/.exec(href);
  if (mdMatch) {
    const file = mdMatch[1] ?? '';
    const slug = file.toLowerCase().replace(/_/g, '-').replace(/-md$/, '');
    return (
      <a
        key={key}
        href={`/docs/${slug}${mdMatch[2] ?? ''}`}
        className="text-primary-600 underline decoration-primary-300 hover:text-primary-700 dark:text-primary-400"
      >
        {label}
      </a>
    );
  }

  // Only http(s), mailto, tel and in-page/root-relative anchors are ever emitted. This rejects
  // `javascript:` and `data:` URLs outright rather than trusting the source document.
  if (!/^(https?:\/\/|mailto:|tel:|#|\/)/i.test(href)) {
    return <span key={key}>{label}</span>;
  }

  const external = /^https?:\/\//i.test(href);
  return (
    <a
      key={key}
      href={href}
      // rel="noopener noreferrer" on every external link, per §12.
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="text-primary-600 underline decoration-primary-300 hover:text-primary-700 dark:text-primary-400"
    >
      {label}
    </a>
  );
}

function splitTableRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

export function renderMarkdown(source: string): RenderedMarkdown {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  const headings: Heading[] = [];
  const seenIds = new Map<string, number>();

  let index = 0;
  let key = 0;

  const uniqueId = (text: string): string => {
    const base = slugify(text) || `section-${key}`;
    const count = seenIds.get(base) ?? 0;
    seenIds.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  };

  while (index < lines.length) {
    const line = lines[index] ?? '';

    // Blank line
    if (line.trim() === '') {
      index += 1;
      continue;
    }

    // Fenced code block
    if (line.trimStart().startsWith('```')) {
      const language = line.trim().slice(3).trim();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? '').trimStart().startsWith('```')) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      index += 1; // consume the closing fence
      blocks.push(<CodeBlock key={`b${key++}`} code={body.join('\n')} language={language} />);
      continue;
    }

    // Horizontal rule
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      blocks.push(
        <hr key={`b${key++}`} className="my-6 border-surface-200 dark:border-surface-700" />,
      );
      index += 1;
      continue;
    }

    // Heading
    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
    if (headingMatch) {
      const level = (headingMatch[1] ?? '#').length;
      const text = (headingMatch[2] ?? '').trim();
      const id = uniqueId(text);
      headings.push({ id, text, level });

      const Tag = `h${level}` as 'h1';
      blocks.push(
        <Tag key={`b${key++}`} id={id} className={HEADING_CLASSES[level]}>
          {renderInline(text, `h${key}`)}
        </Tag>,
      );
      index += 1;
      continue;
    }

    // Pipe table: a header row followed by a separator row.
    if (line.includes('|') && /^\s*\|?[\s:-]+\|[\s:|-]*$/.test(lines[index + 1] ?? '')) {
      const header = splitTableRow(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index] ?? '').includes('|')) {
        rows.push(splitTableRow(lines[index] ?? ''));
        index += 1;
      }

      blocks.push(
        // Wide tables scroll inside their own container rather than making the page scroll.
        <div key={`b${key++}`} className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-surface-300 dark:border-surface-600">
                {header.map((cell, cellIndex) => (
                  <th
                    key={cellIndex}
                    className="px-3 py-2 font-semibold text-surface-900 dark:text-surface-100"
                  >
                    {renderInline(cell, `th${key}-${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-surface-200 dark:border-surface-800">
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-3 py-2 align-top text-surface-700 dark:text-surface-300"
                    >
                      {renderInline(cell, `td${key}-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Blockquote
    if (line.trimStart().startsWith('>')) {
      const body: string[] = [];
      while (index < lines.length && (lines[index] ?? '').trimStart().startsWith('>')) {
        body.push((lines[index] ?? '').trimStart().replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push(
        <blockquote
          key={`b${key++}`}
          className="border-l-4 border-primary-300 bg-primary-50/50 py-2 pl-4 text-sm text-surface-700 dark:border-primary-600 dark:bg-primary-500/5 dark:text-surface-300"
        >
          {renderInline(body.join(' '), `q${key}`)}
        </blockquote>,
      );
      continue;
    }

    // Lists (unordered / ordered), including nested items by indentation.
    const listMatch = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(line);
    if (listMatch) {
      const ordered = /\d/.test(listMatch[2] ?? '');
      const items: ReactNode[] = [];
      let itemIndex = 0;

      while (index < lines.length) {
        const current = lines[index] ?? '';
        const match = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(current);
        if (!match) break;

        const indent = (match[1] ?? '').length;
        items.push(
          <li
            key={itemIndex}
            className="text-sm text-surface-700 dark:text-surface-300"
            style={indent > 0 ? { marginLeft: `${indent / 2}rem` } : undefined}
          >
            {renderInline(match[3] ?? '', `li${key}-${itemIndex}`)}
          </li>,
        );
        itemIndex += 1;
        index += 1;
      }

      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag
          key={`b${key++}`}
          className={`space-y-1 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`}
        >
          {items}
        </ListTag>,
      );
      continue;
    }

    // Paragraph: consume until a blank line or the start of another block.
    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? '';
      if (
        current.trim() === '' ||
        /^(#{1,6})\s/.test(current) ||
        current.trimStart().startsWith('```') ||
        current.trimStart().startsWith('>') ||
        /^(\s*)([-*+]|\d+\.)\s+/.test(current) ||
        /^\s*([-*_])\1{2,}\s*$/.test(current)
      ) {
        break;
      }
      paragraph.push(current);
      index += 1;
    }

    if (paragraph.length > 0) {
      blocks.push(
        <p key={`b${key++}`} className="text-sm leading-relaxed text-surface-700 dark:text-surface-300">
          {renderInline(paragraph.join(' '), `p${key}`)}
        </p>,
      );
    }
  }

  return { blocks, headings };
}
